import {css, StyleSheet, type CSSProperties} from "aphrodite";
import {useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {useTimeout} from "@khanacademy/wonder-blocks-timing";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingSmall,
    LabelLarge,
    LabelXSmall,
} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@repo/analytics-learning-time";
import {useQuery} from "@repo/data-gql";
import {doNotTranslate} from "@repo/i18n";
import {mediaQueries} from "@repo/shared-styles";
import {staticUrl, useLocation} from "@repo/urls";

import {
    PERSONAL_REFERRAL_CODE_QUERY,
    useTeacherReferralEligibilityQuery,
} from "./queries.ts";

import checkIcon from "@phosphor-icons/core/regular/check.svg";
import copyIcon from "@phosphor-icons/core/regular/copy.svg";

type Props = {
    // To apply custom styles to the banner container
    style?: CSSProperties;
    // To hide the image on the left side of the banner
    hideImage?: boolean;
    // To add a custom header text above the banner container
    headerText?: string;
};

// This component wraps ReferralBanner so it can pre-check if the user is
// eligible for the referral program.
// This must be a separate component because you cannot have hooks in a
// conditional statement.
export default function KhanmigoForTeachersReferralBanner(props: Props) {
    const resp = useTeacherReferralEligibilityQuery();
    return (
        resp.status === "success" &&
        resp.data.isEligible && <ReferralBanner {...props} />
    );
}

// This component is responsible for fetching the referral code and displaying
// the Copy and Learn more buttons
export function ReferralContent({
    buttonStyles = {},
}: {
    buttonStyles?: CSSProperties;
}) {
    const [referralCodeResult] = useQuery(PERSONAL_REFERRAL_CODE_QUERY);

    if (referralCodeResult.status === "loading") {
        return <CircularSpinner size="xsmall" />;
    }

    if (
        referralCodeResult.status === "success" &&
        referralCodeResult.data.user?.khanmigoForTeachersReferralCode
    ) {
        const referralCode =
            referralCodeResult.data.user?.khanmigoForTeachersReferralCode;

        return (
            <>
                <ReferralButton
                    referralCode={referralCode}
                    style={buttonStyles}
                />
                <Link
                    href="/about/docs/us-teacher-giveaway-summer-2024"
                    style={styles.learnMore}
                >
                    {doNotTranslate`Learn more`}
                </Link>
            </>
        );
    }

    return (
        <LabelXSmall className={css(styles.error)}>
            {doNotTranslate`Unable to load your personal referral link.`}
        </LabelXSmall>
    );
}

// Renders the banner with the referral code.
function ReferralBanner({style, hideImage, headerText}: Props) {
    return (
        <>
            {headerText && (
                <>
                    <HeadingSmall tag="h2">{headerText}</HeadingSmall>
                    <Strut size={spacing.medium_16} />
                </>
            )}
            <div className={css(styles.bannerFrame, style)}>
                {hideImage !== true && (
                    <img
                        src={staticUrl(
                            "/images/khanmigo-for-teachers-referrals/khanmigo-collage-2x-v2.png",
                        )}
                        alt=""
                        className={css(styles.collage)}
                    />
                )}
                <div className={css(styles.copy)}>
                    <LabelLarge style={[styles.headline]}>
                        {doNotTranslate`Refer and win!`}
                    </LabelLarge>
                    <LabelXSmall style={styles.paragraph}>
                        {doNotTranslate`Share Khanmigo with your teacher friends and be entered into this month’s draw to win prizes when they sign up!`}
                    </LabelXSmall>
                    <div aria-live="off">
                        <ReferralContent />
                    </div>
                </div>
            </div>
        </>
    );
}

function ReferralButton({
    referralCode,
    style = {},
}: {
    referralCode: string;
    style?: CSSProperties;
}) {
    const [copied, setCopied] = useState(false);
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const location = useLocation();

    const resetCopyTimeout = useTimeout(() => {
        setCopied(false);
    }, 3000);

    return (
        <Button
            startIcon={copied ? checkIcon : copyIcon}
            size="small"
            style={style}
            onClick={() => {
                window.navigator.clipboard.writeText(
                    `khanacademy.org/referrals/${referralCode}`,
                );
                publishAnalyticsEvent({
                    eventType: "AiTeacherToolsReferralLinkCopied",
                    eventSchemaVersion: 1,
                    pageUrl: location.href,
                    referralCode,
                });
                setCopied(true);
                resetCopyTimeout.set();
            }}
        >
            {copied
                ? doNotTranslate`Link copied`
                : doNotTranslate`Copy your referral link`}
        </Button>
    );
}

const styles = StyleSheet.create({
    bannerFrame: {
        display: "flex",
        alignItems: "start",
        maxWidth: 561,
        [mediaQueries.xsOrSmaller]: {
            flexWrap: "wrap",
            textAlign: "center",
            justifyContent: "center",
        },
    },
    collage: {
        marginRight: 30,
        maxWidth: 190,
        alignSelf: "center",
    },
    copy: {
        maxWidth: 342,
    },
    learnMore: {
        marginLeft: spacing.small_12,
    },
    headline: {
        marginTop: spacing.xxxSmall_4,
        marginBottom: spacing.small_12,
    },
    paragraph: {
        marginTop: spacing.small_12,
        marginBottom: spacing.small_12,
    },
    error: {
        fontStyle: "italic",
        color: color.red,
    },
});
